import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Home,
  Search,
  Wrench,
  PlusCircle,
  Pencil,
  Trash2,
  X,
  MoreVertical,
  Users,
  UserPlus,
  CheckCircle2,
} from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ServicioAsignacion,
  ServicioCatalogo,
  SolicitudServicioItem,
  actualizarServicio,
  aprobarSolicitudServicio,
  eliminarServicio,
  listarSolicitudesServicio,
  obtenerAsignacionesDeServicio,
  obtenerServiciosCatalogo,
} from "../services/serviciosAsociacionService.ts";
import AsignarServicioModal from "./AsignarServicioModal.tsx";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import { SERVICE_ICON_OPTIONS, getServiceIcon } from "../utils/serviceIcons.ts";

export default function ServiciosGestor() {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const navigate = useNavigate();
  const [servicios, setServicios] = useState<ServicioCatalogo[]>([]);
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [servicioSeleccionado, setServicioSeleccionado] = useState<ServicioCatalogo | null>(null);
  const [servicioEditando, setServicioEditando] = useState<ServicioCatalogo | null>(null);
  const [editForm, setEditForm] = useState({
    nombre: "",
    categoria: "",
    descripcion: "",
    icono: "auto",
    activo: true,
  });
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [eliminandoId, setEliminandoId] = useState<number | null>(null);
  const [menuServicioId, setMenuServicioId] = useState<number | null>(null);
  const [tabActivo, setTabActivo] = useState<"activos" | "inactivos" | "pendientes">("activos");
  const [confirmacionServicio, setConfirmacionServicio] = useState<ServicioCatalogo | null>(null);
  const [asignados, setAsignados] = useState<ServicioAsignacion[]>([]);
  const [cargandoAsignados, setCargandoAsignados] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [servicioDetalle, setServicioDetalle] = useState<ServicioCatalogo | null>(null);
  const [solicitudesPendientes, setSolicitudesPendientes] = useState<SolicitudServicioItem[]>([]);
  const [cargandoSolicitudes, setCargandoSolicitudes] = useState(false);
  const [aprobandoSolicitudId, setAprobandoSolicitudId] = useState<number | null>(null);

  useEffect(() => {
    const loadServicios = async () => {
      setCargando(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const data = await obtenerServiciosCatalogo(token);
        const normalizados = Array.isArray(data)
          ? data.map((s: ServicioCatalogo) => ({
              ...s,
              id: (s.id ?? s.servicio_id) as number,
              activo:
                s.activo === false ||
                s.activo === 0 ||
                s.activo === "0" ||
                s.activo === "false"
                  ? false
                  : true,
            }))
          : [];
        setServicios(normalizados);
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los servicios.");
      } finally {
        setCargando(false);
      }
    };
    loadServicios();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const loadSolicitudes = async () => {
      setCargandoSolicitudes(true);
      try {
        const token = await getAccessTokenSilently();
        const data = await listarSolicitudesServicio({ estado: 'pendiente' }, token);
        setSolicitudesPendientes(Array.isArray(data) ? data : []);
      } catch {
        setSolicitudesPendientes([]);
      } finally {
        setCargandoSolicitudes(false);
      }
    };
    loadSolicitudes();
  }, [getAccessTokenSilently]);

  const aprobarSolicitud = async (solicitud: SolicitudServicioItem) => {
    if (!solicitud?.solicitud_id) return;
    try {
      setAprobandoSolicitudId(solicitud.solicitud_id);
      const token = await getAccessTokenSilently();
      await aprobarSolicitudServicio(solicitud.solicitud_id, token);
      setSolicitudesPendientes((prev) => prev.filter((s) => s.solicitud_id !== solicitud.solicitud_id));
      setMensaje(`Solicitud de ${solicitud.nombre} aprobada.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'No se pudo aprobar la solicitud');
    } finally {
      setAprobandoSolicitudId(null);
    }
  };

  const filtrados = useMemo(() => {
    if (!filtro.trim()) return servicios;
    const q = filtro.toLowerCase();
    return servicios.filter(
      (s) =>
        s.nombre?.toLowerCase().includes(q) ||
        s.categoria?.toLowerCase().includes(q) ||
        s.descripcion?.toLowerCase().includes(q)
    );
  }, [servicios, filtro]);

  const filtradosPorTab = useMemo(() => {
    const categoryOrder = [
      "salud",
      "legal",
      "administrativos",
      "economicos",
      "administrativos y economicos",
      "logistica",
      "capacitacion",
      "formacion",
      "espacios",
      "alquiler de salas",
      "gestiones",
      "tramites",
      "promocion comercial",
      "vinculacion educativa",
      "educacion",
      "becas",
    ];
    const getOrderIndex = (categoria?: string) => {
      const value = (categoria || "").toLowerCase();
      const idx = categoryOrder.findIndex((key) => value.includes(key));
      return idx === -1 ? categoryOrder.length : idx;
    };
    return filtrados
      .filter((s) => {
        if (tabActivo === "activos") return s.activo !== false;
        if (tabActivo === "inactivos") return s.activo === false;
        return false;
      })
      .slice()
      .sort((a, b) => {
        const orderDiff = getOrderIndex(a.categoria) - getOrderIndex(b.categoria);
        if (orderDiff !== 0) return orderDiff;
        return (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" });
      });
  }, [filtrados, tabActivo]);

  const abrirModalServicio = async (servicio: ServicioCatalogo, cargarAsignados: boolean) => {
    setMensaje(null);
    setError(null);
    setAsignados([]);
    setServicioSeleccionado(servicio);
    setMenuServicioId(null);
    if (cargarAsignados) {
      setCargandoAsignados(true);
      try {
        const token = await getAccessTokenSilently();
        const data = await obtenerAsignacionesDeServicio(servicio.id, token);
        setAsignados(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los socios asignados.");
      } finally {
        setCargandoAsignados(false);
      }
    }
  };

  const abrirEdicionServicio = (servicio: ServicioCatalogo) => {
    setMensaje(null);
    setError(null);
    setServicioEditando(servicio);
    setMenuServicioId(null);
    setEditForm({
      nombre: servicio.nombre || "",
      categoria: servicio.categoria || "",
      descripcion: servicio.descripcion || "",
      icono: servicio.icono || "auto",
      activo: Boolean(servicio.activo),
    });
  };

  const guardarEdicionServicio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!servicioEditando?.id) return;
    setError(null);
    setMensaje(null);
    if (!editForm.nombre.trim()) {
      setError("Ingresá un nombre para el servicio.");
      return;
    }
    setGuardandoEdicion(true);
    try {
      const token = await getAccessTokenSilently();
      await actualizarServicio(
        servicioEditando.id,
        {
          nombre: editForm.nombre.trim(),
          categoria: editForm.categoria.trim() || undefined,
          descripcion: editForm.descripcion.trim() || undefined,
          icono: editForm.icono !== "auto" ? editForm.icono : undefined,
          activo: editForm.activo,
        },
        token
      );
      setServicios((prev) =>
        prev.map((s) =>
          s.id === servicioEditando.id
            ? {
                ...s,
                nombre: editForm.nombre.trim(),
                categoria: editForm.categoria.trim() || undefined,
                descripcion: editForm.descripcion.trim() || undefined,
                icono: editForm.icono !== "auto" ? editForm.icono : undefined,
                activo: editForm.activo,
              }
            : s
        )
      );
      setMensaje(`Servicio "${editForm.nombre.trim()}" actualizado.`);
      setServicioEditando(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo actualizar el servicio.");
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const borrarServicio = async (servicio: ServicioCatalogo) => {
    if (!servicio.id) return;
    setMenuServicioId(null);
    setEliminandoId(servicio.id);
    setError(null);
    setMensaje(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await eliminarServicio(servicio.id, token);
      const activo =
        data?.activo === false ||
        data?.activo === 0 ||
        data?.activo === "0" ||
        data?.activo === "false"
          ? false
          : false;
      setServicios((prev) =>
        prev.map((s) =>
          s.id === servicio.id
            ? {
                ...s,
                activo,
                nombre: data?.nombre ?? s.nombre,
                categoria: data?.categoria ?? s.categoria,
                descripcion: data?.descripcion ?? s.descripcion,
              }
            : s
        )
      );
      setMensaje(`Servicio "${servicio.nombre}" dado de baja.`);
      if (servicioSeleccionado?.id === servicio.id) setServicioSeleccionado(null);
      if (servicioEditando?.id === servicio.id) setServicioEditando(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo dar de baja el servicio.");
    } finally {
      setEliminandoId(null);
    }
  };

  const activarServicio = async (servicio: ServicioCatalogo) => {
    if (!servicio.id) return;
    setMenuServicioId(null);
    setEliminandoId(servicio.id);
    setError(null);
    setMensaje(null);
    try {
      const token = await getAccessTokenSilently();
      const data = await actualizarServicio(servicio.id, { activo: true }, token);
      setServicios((prev) =>
        prev.map((s) =>
          s.id === servicio.id
            ? {
                ...s,
                activo:
                  data?.activo === false ||
                  data?.activo === 0 ||
                  data?.activo === "0" ||
                  data?.activo === "false"
                    ? false
                    : true,
                nombre: data?.nombre ?? s.nombre,
                categoria: data?.categoria ?? s.categoria,
                descripcion: data?.descripcion ?? s.descripcion,
              }
            : s
        )
      );
      setMensaje(`Servicio "${servicio.nombre}" activado.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "No se pudo activar el servicio.");
    } finally {
      setEliminandoId(null);
    }
  };

  if (!isAdmin) return <Navigate to="/inicio" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10 space-y-6">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <Wrench className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Gestionar servicios</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 p-6 text-white shadow-2xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-100">Administración</p>
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">Asignar servicios a socios</h1>
              <p className="text-sm text-indigo-50">
                Buscá en el catálogo y asigná servicios a cualquier socio. Solo administradores.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-2xl bg-white/15 p-3 text-sm font-semibold text-white shadow-sm backdrop-blur">
                Servicios disponibles: {servicios.length}
              </div>
              <button
                type="button"
                onClick={() => navigate("/servicios-nuevo")}
                className="inline-flex items-center gap-2 rounded-xl bg-white text-indigo-700 px-3 py-2 text-sm font-semibold shadow-sm transition hover:bg-indigo-50"
              >
                <PlusCircle className="h-4 w-4" />
                Crear servicio
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900">Catálogo</p>
              <p className="text-xs text-slate-500">Seleccioná un servicio y asignalo a un socio.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-full bg-slate-100 p-1 text-xs font-semibold text-slate-600">
                <button
                  type="button"
                  onClick={() => setTabActivo("activos")}
                  className={`rounded-full px-3 py-1 transition ${
                    tabActivo === "activos" ? "bg-white text-slate-900 shadow-sm" : "hover:text-slate-800"
                  }`}
                >
                  Activos ({servicios.filter((s) => s.activo !== false).length})
                </button>
                <button
                  type="button"
                  onClick={() => setTabActivo("inactivos")}
                  className={`rounded-full px-3 py-1 transition ${
                    tabActivo === "inactivos" ? "bg-white text-slate-900 shadow-sm" : "hover:text-slate-800"
                  }`}
                >
                  Inactivos ({servicios.filter((s) => s.activo === false).length})
                </button>
                <button
                  type="button"
                  onClick={() => setTabActivo("pendientes")}
                  className={`rounded-full px-3 py-1 transition ${
                    tabActivo === "pendientes" ? "bg-white text-slate-900 shadow-sm" : "hover:text-slate-800"
                  }`}
                >
                  Pendientes ({solicitudesPendientes.length})
                </button>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <Search className="h-4 w-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o categoría"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="w-52 bg-transparent text-sm text-slate-800 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {mensaje && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{mensaje}</div>}
          {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          {cargandoAsignados && (
            <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-800">Cargando socios asignados…</div>
          )}

          {tabActivo === "pendientes" ? (
            <div className="space-y-3">
              {cargandoSolicitudes ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">Cargando solicitudes pendientes...</div>
              ) : solicitudesPendientes.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">No hay solicitudes pendientes por ahora.</div>
              ) : (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  {(() => {
                    const s = solicitudesPendientes[0];
                    return (
                      <div key={s.solicitud_id} className="rounded-xl border border-amber-200 bg-white p-3">
                        <p className="text-sm font-semibold text-slate-900">{s.servicio}</p>
                        <p className="text-xs text-slate-600">{s.nombre} · {s.email}</p>
                        <p className="text-xs text-slate-500">Socio ID: {s.socio_id || '—'} · Servicio ID: {s.servicio_id || '—'}</p>
                        <button
                          type="button"
                          onClick={() => aprobarSolicitud(s)}
                          disabled={aprobandoSolicitudId === s.solicitud_id}
                          className="mt-2 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {aprobandoSolicitudId === s.solicitud_id ? 'Aprobando...' : 'Aprobar'}
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : cargando ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm animate-pulse">
                  <div className="h-4 w-28 rounded bg-slate-200" />
                  <div className="mt-2 h-6 w-40 rounded bg-slate-200" />
                  <div className="mt-3 h-10 w-full rounded bg-slate-200" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {filtradosPorTab.map((servicio) => (
                <div
                  key={servicio.id ?? servicio.servicio_id}
                  className="relative flex h-full flex-col justify-between overflow-visible rounded-2xl border border-slate-200 bg-white p-5 pt-8 shadow-sm"
                >
                  <div className="absolute -left-3 -top-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 text-white shadow-lg border-2 border-blue-900/60 drop-shadow-lg">
                      {(() => {
                        const Icon = getServiceIcon(servicio.icono, servicio.categoria);
                        return <Icon className="h-6 w-6" strokeWidth={2.2} />;
                      })()}
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {servicio.categoria || "Servicio"}
                    </p>
                    <h3
                      className="text-lg font-semibold text-slate-900"
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.35rem",
                        maxHeight: "2.7rem",
                      }}
                    >
                      {servicio.nombre}
                    </h3>
                  </div>
                  <p
                    className="mt-3 text-center text-sm text-slate-600"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      lineHeight: "1.25rem",
                      maxHeight: "5rem",
                    }}
                  >
                    {servicio.descripcion || "Sin descripción"}
                  </p>
                  <div className="mt-4 flex items-center justify-end">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setServicioDetalle(servicio)}
                        className="rounded-full bg-sky-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-sky-600"
                      >
                        Detalle
                      </button>
                      <div className="relative">
                      {(() => {
                        const servicioId = (servicio.id ?? servicio.servicio_id) as number | undefined;
                        const menuAbierto = servicioId != null && menuServicioId === servicioId;
                        return (
                          <>
                      <button
                        type="button"
                        onClick={() =>
                          setMenuServicioId((prev) => (prev === servicioId ? null : (servicioId ?? null)))
                        }
                        className="inline-flex items-center justify-center rounded-full bg-slate-100 p-2 text-slate-600 shadow-sm transition hover:bg-slate-200"
                        aria-haspopup="menu"
                        aria-expanded={menuAbierto}
                        title="Más acciones"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                      {menuAbierto && (
                        <div
                          className="absolute right-0 z-10 mt-2 w-52 rounded-xl border border-slate-200 bg-white p-1 shadow-xl"
                          role="menu"
                        >
                          <button
                            type="button"
                            onClick={() => abrirModalServicio(servicio as ServicioCatalogo, true)}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            role="menuitem"
                          >
                            <Users className="h-4 w-4 text-slate-500" />
                            Ver socios
                          </button>
                          <button
                            type="button"
                            onClick={() => abrirModalServicio(servicio as ServicioCatalogo, false)}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            role="menuitem"
                          >
                            <UserPlus className="h-4 w-4 text-slate-500" />
                            Asignar a socio
                          </button>
                          <button
                            type="button"
                            onClick={() => abrirEdicionServicio(servicio as ServicioCatalogo)}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            role="menuitem"
                          >
                            <Pencil className="h-4 w-4 text-slate-500" />
                            Editar servicio
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              servicio.activo
                                ? setConfirmacionServicio(servicio as ServicioCatalogo)
                                : activarServicio(servicio as ServicioCatalogo)
                            }
                            disabled={eliminandoId === servicioId}
                            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold ${
                              servicio.activo ? "text-rose-700 hover:bg-rose-50" : "text-emerald-700 hover:bg-emerald-50"
                            } disabled:cursor-not-allowed disabled:opacity-60`}
                            role="menuitem"
                          >
                            {servicio.activo ? (
                              <Trash2 className="h-4 w-4 text-rose-500" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            )}
                            {eliminandoId === servicioId
                              ? servicio.activo
                                ? "Dando de baja..."
                                : "Activando..."
                              : servicio.activo
                                ? "Dar de baja"
                                : "Activar"}
                          </button>
                        </div>
                      )}
                          </>
                        );
                      })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!filtradosPorTab.length && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600 sm:col-span-2 xl:col-span-3">
                  No hay servicios para mostrar.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {servicioSeleccionado && (
        <AsignarServicioModal
          servicio={{
            id: servicioSeleccionado.id,
            nombre: servicioSeleccionado.nombre,
            categoria: servicioSeleccionado.categoria,
            descripcion: servicioSeleccionado.descripcion,
          }}
          onClose={() => setServicioSeleccionado(null)}
          onAsignado={() => {
            setMensaje(`Servicio ${servicioSeleccionado.nombre} asignado correctamente.`);
            setServicioSeleccionado(null);
          }}
          sociosAsignados={asignados}
          loadingSociosAsignados={cargandoAsignados}
        />
      )}

      {servicioEditando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur transition-opacity duration-200 ease-out">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ease-out animate-[fadeIn_.2s_ease-out]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Edición</p>
                <h2 className="text-xl font-bold text-slate-900">Editar servicio</h2>
              </div>
              <button
                type="button"
                onClick={() => setServicioEditando(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={guardarEdicionServicio} className="mt-5 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">
                  Nombre
                  <input
                    type="text"
                    value={editForm.nombre}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, nombre: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </label>
                <label className="text-sm font-semibold text-slate-700">
                  Categoría
                  <input
                    type="text"
                    value={editForm.categoria}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, categoria: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Ej: Seguridad, Salud..."
                  />
                </label>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Icono</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {SERVICE_ICON_OPTIONS.map(({ key, label, Icon }) => {
                    const selected = editForm.icono === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setEditForm((prev) => ({ ...prev, icono: key }))}
                        className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-semibold transition ${
                          selected
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                        aria-pressed={selected}
                      >
                        <Icon className="h-5 w-5" strokeWidth={2} />
                        <span className="text-[11px]">{label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">Elegi un icono para el servicio.</p>
              </div>
              <label className="text-sm font-semibold text-slate-700">
                Descripción
                <textarea
                  value={editForm.descripcion}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                  className="mt-1 min-h-[120px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Opcional: breve detalle del servicio."
                />
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="edit-activo"
                  type="checkbox"
                  checked={editForm.activo}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, activo: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="edit-activo" className="text-sm font-semibold text-slate-700">
                  Activo
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setServicioEditando(null)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardandoEdicion}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <Pencil className="h-4 w-4" />
                  {guardandoEdicion ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmacionServicio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur transition-opacity duration-200 ease-out">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ease-out animate-[fadeIn_.2s_ease-out]">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                <Trash2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Dar de baja servicio</h3>
                <p className="mt-1 text-sm text-slate-600">
                  ¿Dar de baja el servicio &quot;{confirmacionServicio.nombre}&quot;? Quedará como
                  inactivo.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConfirmacionServicio(null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmacionServicio(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const servicio = confirmacionServicio;
                  setConfirmacionServicio(null);
                  borrarServicio(servicio);
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700"
              >
                Dar de baja
              </button>
            </div>
          </div>
        </div>
      )}

      {servicioDetalle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4 py-8 backdrop-blur transition-opacity duration-200 ease-out">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ease-out animate-[fadeIn_.2s_ease-out]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Detalle</p>
                <h3 className="text-xl font-bold text-slate-900">{servicioDetalle.nombre}</h3>
                <p className="text-sm text-slate-600">
                  {servicioDetalle.categoria || "Servicio"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setServicioDetalle(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {servicioDetalle.descripcion || "Sin descripción"}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                  Estado: {servicioDetalle.activo ? "Activo" : "Inactivo"}
                </span>
                {servicioDetalle.icono && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                    Icono: {servicioDetalle.icono}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setServicioDetalle(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
